import React, { useEffect, useState, useCallback } from 'react';
import { TextField } from '@mui/material';
import { Lock, Logout, Save, Upload, Undo, CheckCircle, Warning, History } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/client';
import NotificationPopup from '../../component/NotificationPopup';
import ChangePasswordDialog from '../../../modals/ChangePasswordDialog';
import Pagination from '../../component/Pagination';
import { getFormattedTimeUntilExpiration, getTokenSessionProgress } from '../../../../utils/tokenUtils';
import LogoutConfirmDialog from '../../component/LogoutConfirmDialog';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';

const Profile = ({ onLogout }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        profile_picture: null,
    });

    const [originalFormData, setOriginalFormData] = useState({}); // Track original data
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [tokenExpiration, setTokenExpiration] = useState('');
    const [sessionProgress, setSessionProgress] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [originalImage, setOriginalImage] = useState(null);
    const [imageToRemove, setImageToRemove] = useState(false);
    const role = localStorage.getItem('role');

    const [activeTab, setActiveTab] = useState('profile');
    const [loginActivityList, setLoginActivityList] = useState([]);
    const [loginActivityLoading, setLoginActivityLoading] = useState(false);
    const [loginActivityError, setLoginActivityError] = useState(null);
    const [loginActivityPage, setLoginActivityPage] = useState(1);
    const [loginActivityPageSize, setLoginActivityPageSize] = useState(5);

    const authHeader = () => {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (!token) return {};
        // Ensure Bearer prefix is only added if not already present
        return {
            Authorization: token.startsWith('Bearer ') || token.startsWith('Token ') ? token : `Bearer ${token}`,
        };
    };

    useEffect(() => {
        fetchProfileDetails();
    }, []);

    // Update token expiration and session progress
    useEffect(() => {
        const updateTokenExpiration = () => {
            const token = localStorage.getItem('token');
            if (token) {
                setTokenExpiration(getFormattedTimeUntilExpiration(token));
                setSessionProgress(getTokenSessionProgress(token));
            }
        };

        updateTokenExpiration();
        const interval = setInterval(updateTokenExpiration, 30000);
        return () => clearInterval(interval);
    }, []);

    // Function to check if there are any changes
    const hasChanges = () => {
        // Check text fields
        const textFieldsChanged = originalFormData.firstName !== formData.firstName ||
            originalFormData.lastName !== formData.lastName ||
            originalFormData.company !== formData.company;

        // Check image changes
        const imageChanged = imagePreview !== null || imageToRemove;

        return textFieldsChanged || imageChanged;
    };

    const fetchLoginActivity = useCallback(async () => {
        setLoginActivityLoading(true);
        setLoginActivityError(null);
        try {
            const res = await api.get('profile/login-activity/', { headers: authHeader() });
            const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
            setLoginActivityList(data);
        } catch (err) {
            console.error('Error fetching login activity:', err);
            setLoginActivityError(err.message || 'Failed to load login activity');
            setLoginActivityList([]);
        } finally {
            setLoginActivityLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'login_activity') {
            fetchLoginActivity();
        }
    }, [activeTab, fetchLoginActivity]);

    const fetchProfileDetails = async () => {
        try {
            const res = await api.get('profile/', {
                headers: authHeader(),
            });

            const userData = res.data.results || res.data;

            let profileImageUrl = null;
            if (userData?.profile_picture) {
                if (userData.profile_picture.startsWith('http')) {
                    profileImageUrl = userData.profile_picture;
                } else if (userData.profile_picture.startsWith('/')) {
                    profileImageUrl = `${process.env.REACT_APP_FRONTEND_URL}${userData.profile_picture}`;
                } else {
                    profileImageUrl = `${process.env.REACT_APP_FRONTEND_URL}/${userData.profile_picture}`;
                }
            }

            const profileData = {
                firstName: userData?.firstName || "",
                lastName: userData?.lastName || "",
                email: userData?.email || "",
                company: userData?.company || "",
                profile_picture: profileImageUrl,
            };

            setFormData(profileData);
            setOriginalFormData(profileData); // Store original data
            setOriginalImage(profileImageUrl);
            setImagePreview(null);
            setImageToRemove(false);
        } catch (err) {
            console.error('Error fetching User Profile:', err);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = t('profile.firstNameRequired');
        if (!formData.lastName.trim()) newErrors.lastName = t('profile.lastNameRequired');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({
                ...prev,
                profile_picture: file,
            }));

            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            setImageToRemove(false);
        }
    };

    const handleRemoveCurrentImage = () => {
        setImageToRemove(true);
        setImagePreview(null);
        setFormData(prev => ({
            ...prev,
            profile_picture: null
        }));
        const fileInput = document.querySelector('input[name="profile_picture"]');
        if (fileInput) fileInput.value = '';
    };

    const handleUpdate = async () => {
        try {
            if (validate()) {
                const formPayload = new FormData();
                formPayload.append('firstName', formData.firstName);
                formPayload.append('lastName', formData.lastName);

                if (role !== 'Candidate') {
                    formPayload.append('company', formData.company);
                }

                if (imageToRemove) {
                    formPayload.append('remove_profile_picture', 'true');
                } else if (formData.profile_picture && typeof formData.profile_picture !== 'string') {
                    formPayload.append('profile_picture', formData.profile_picture);
                }

                const res = await api.put('profile/', formPayload, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (res && res.data) {
                    // Assuming the API returns the updated user object
                    const updatedUser = res.data.user_details || res.data;

                    // --- KEY CHANGE HERE ---
                    // Get the current user data from localStorage
                    const currentUserData = JSON.parse(localStorage.getItem('user'));

                    // Merge the old data with the new profile picture URL
                    const mergedUser = {
                        ...currentUserData,
                        ...updatedUser,
                        user_profile_picture: updatedUser.profile_picture || updatedUser.user_profile_picture
                    };

                    // Update localStorage with the full, merged user object
                    localStorage.setItem('user', JSON.stringify(mergedUser));

                    // Dispatch the event as a secondary measure for immediate updates on the same page
                    const event = new CustomEvent('profilePhotoUpdated', {
                        detail: { user: mergedUser }
                    });
                    window.dispatchEvent(event);

                    // ... rest of your success logic
                    setSnackbar({ open: true, message: t('profile.profileUpdatedSuccessfully'), severity: 'success' });
                    setImagePreview(null);
                    setImageToRemove(false);
                    fetchProfileDetails();
                    window.location.reload()
                }
            }
        } catch (err) {
            console.log("err", err);
            setSnackbar({ open: true, message: err.message, severity: 'error' });
        }
    };

    const handleCancel = () => {
        // Reset to original data
        setFormData({ ...originalFormData });
        setErrors({});

        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
            setImagePreview(null);
        }

        setImageToRemove(false);

        // Reset file input
        const fileInput = document.querySelector('input[name="profile_picture"]');
        if (fileInput) fileInput.value = '';
    };

    const handlePasswordSuccess = (message) => {
        setSnackbar({
            open: true,
            message,
            severity: 'success'
        });
    };

    const handlePasswordError = (message) => {
        setSnackbar({
            open: true,
            message,
            severity: 'error'
        });
    };

    const handleClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleLogoutConfirm = () => {
        setLogoutDialogOpen(false);
        if (onLogout) {
            onLogout();
        }
    };

    const getFormFields = () => {
        return [
            { label: t('profile.firstName'), name: 'firstName', type: 'text' },
            { label: t('profile.lastName'), name: 'lastName', type: 'text' },
        ];
    };

    return (
        <PageShell fill={false}>
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.7; }
                    }
                    .bg-gradient-primary {
                        background: linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%);
                    }
                    .hover-gradient:hover {
                        background: linear-gradient(135deg, #0284c7 0%, #c026d3 100%);
                    }
                    .glass-effect {
                        backdrop-filter: blur(10px);
                        background: rgba(255, 255, 255, 0.8);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                `}
            </style>

            <PageCard overflow={false}>
                    <PageHeader
                        title={t('profile.title')}
                        subtitle={t('profile.subtitle')}
                    >
                        <PageHeaderButton
                            onClick={() => setPasswordDialogOpen(true)}
                            icon={<Lock className="h-4 w-4" />}
                        >
                            {t('profile.changePassword')}
                        </PageHeaderButton>
                    </PageHeader>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 px-8 pt-2">
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveTab('profile')}
                                className={`px-5 py-3 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${activeTab === 'profile'
                                    ? 'border-blue-600 text-blue-600 bg-white'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                {t('profile.title')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('login_activity')}
                                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${activeTab === 'login_activity'
                                    ? 'border-blue-600 text-blue-600 bg-white'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                <History className="w-4 h-4" />
                                {t('profile.loginActivityTab')}
                            </button>
                        </div>
                    </div>

                    {/* Tab content: Profile */}
                    {activeTab === 'profile' && (
                        <div className="p-8">
                            <div className="space-y-8">
                                {/* Form Fields Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {getFormFields().map((field) => (
                                        <div key={field.name} className="space-y-2">
                                            <TextField
                                                fullWidth
                                                type={field.type}
                                                label={field.label}
                                                name={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                error={!!errors[field.name]}
                                                helperText={errors[field.name]}
                                                className="bg-white"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        backgroundColor: 'white',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            '& fieldset': {
                                                                borderColor: '#0ea5e9',
                                                            },
                                                        },
                                                        '&.Mui-focused': {
                                                            '& fieldset': {
                                                                borderColor: '#0ea5e9',
                                                                borderWidth: '2px',
                                                            },
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root.Mui-focused': {
                                                        color: '#0ea5e9',
                                                    },
                                                }}
                                            />
                                        </div>
                                    ))}

                                    {/* Enhanced Profile Picture Upload */}
                                    <div className="space-y-4">
                                        <label className="block text-lg font-semibold text-gray-800 mb-3">
                                            {t('profile.profilePicture')}
                                        </label>

                                        {/* Custom File Input with Image Preview */}
                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                name="profile_picture"
                                                onChange={handleFileChange}
                                                className="hidden"
                                                id="profile-picture-upload"
                                            />

                                            {/* Choose Image Box - shows image when available */}
                                            <label
                                                htmlFor="profile-picture-upload"
                                                className={`
                                                relative flex items-center justify-center w-32 h-32 border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer overflow-hidden group
                                                ${!imageToRemove && (imagePreview || originalImage)
                                                        ? 'border-green-300 bg-green-50/50 hover:border-green-500 hover:bg-green-100/50'
                                                        : 'border-blue-300 bg-blue-50/50 hover:border-blue-500 hover:bg-blue-100/50'
                                                    }
                                            `}
                                            >
                                                {/* Show image if available and not marked for removal */}
                                                {!imageToRemove && (imagePreview || originalImage) ? (
                                                    <div className="relative w-full h-full">
                                                        <img
                                                            src={imagePreview || originalImage || (typeof formData.profile_picture === 'string' ? formData.profile_picture : '')}
                                                            alt={t('profile.profilePreview')}
                                                            className="w-full h-full object-cover rounded-lg"
                                                            onError={(e) => {
                                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiAxMkM5Ljc5IDEyIDggMTAuMjEgOCA4UzkuNzkgNCAxMiA0IDE2IDUuNzkgMTYgOCAxNC4yMSAxMiAxMiAxMlpNMTIgMTRDMTUuMzEgMTQgMTggMTUuNzkgMTggMThWMjBINlYxOEM2IDE1Ljc5IDguNjkgMTQgMTIgMTRaIiBmaWxsPSIjOUI5QjlCIi8+Cjwvc3ZnPgo=';
                                                                e.target.onerror = null;
                                                            }}
                                                        />

                                                        {/* Overlay on hover */}
                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                            <div className="text-white text-center">
                                                                <Upload className="w-6 h-6 mx-auto mb-1" />
                                                                <span className="text-sm font-medium">{t('profile.changeImage')}</span>
                                                            </div>
                                                        </div>

                                                        {/* Status badge */}
                                                        <div className="absolute top-2 left-2">
                                                            {imagePreview ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-500 text-white rounded-full">
                                                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                                                                    {t('profile.new')}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-500 text-white rounded-full">
                                                                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                                                    {t('profile.current')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* Show upload prompt when no image */
                                                    <div className="text-center">
                                                        <Upload className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                                        <span className="text-blue-700 font-medium">{t('profile.chooseImage')}</span>
                                                        <p className="text-sm text-gray-500 mt-1">{t('profile.imageFormats')}</p>
                                                    </div>
                                                )}
                                            </label>
                                        </div>

                                        {/* Image Information and Actions */}
                                        {!imageToRemove && (imagePreview || originalImage) && (
                                            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                                                {/* File name for new uploads */}
                                                {imagePreview && formData.profile_picture && typeof formData.profile_picture !== 'string' && (
                                                    <p className="text-sm text-gray-600 flex items-center gap-2">
                                                        <span>📁</span>
                                                        <span className="font-medium">{formData.profile_picture.name}</span>
                                                        <span className="text-xs text-gray-400">
                                                            ({(formData.profile_picture.size / 1024 / 1024).toFixed(2)} MB)
                                                        </span>
                                                    </p>
                                                )}

                                                {/* Action buttons */}
                                                <div className="flex gap-2 flex-wrap">
                                                    {imagePreview && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                URL.revokeObjectURL(imagePreview);
                                                                setImagePreview(null);
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    profile_picture: originalImage
                                                                }));
                                                                const fileInput = document.querySelector('input[name="profile_picture"]');
                                                                if (fileInput) fileInput.value = '';
                                                            }}
                                                            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                                                        >
                                                            {t('profile.cancelSelection')}
                                                        </button>
                                                    )}

                                                    {!imagePreview && originalImage && (
                                                        <button
                                                            type="button"
                                                            onClick={handleRemoveCurrentImage}
                                                            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                                                        >
                                                            {t('profile.removeCurrent')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Removal pending state */}
                                        {imageToRemove && (
                                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                                <div className="flex items-center gap-2 text-red-700 mb-2">
                                                    <span className="font-medium">{t('profile.imageWillBeRemoved')}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setImageToRemove(false);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            profile_picture: originalImage
                                                        }));
                                                    }}
                                                    className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                                                >
                                                    <Undo className="w-4 h-4" />
                                                    {t('profile.undoRemoval')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons - Only show when there are changes */}
                                {hasChanges() && (
                                    <div className="flex justify-center gap-4 pt-8 border-t border-gray-100">
                                        <button
                                            onClick={handleCancel}
                                            className="inline-flex items-center gap-2 px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 font-medium shadow-sm hover:shadow-md transform hover:scale-105"
                                        >
                                            {t('profile.cancelChanges')}
                                        </button>
                                        <button
                                            onClick={handleUpdate}
                                            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-primary text-white rounded-xl hover-gradient transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                                        >
                                            <Save className="w-5 h-5" />
                                            {t('profile.saveChanges')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tab content: Login activity */}
                    {activeTab === 'login_activity' && (
                        <div className="p-8">
                            <h2 className="text-lg font-semibold text-gray-800 mb-1">{t('profile.loginActivityTitle')}</h2>
                            <p className="text-sm text-gray-500 mb-6">{t('profile.loginActivitySubtitle')}</p>
                            {loginActivityLoading ? (
                                <p className="text-gray-500 py-8">{t('profile.loginActivityLoading')}</p>
                            ) : loginActivityError ? (
                                <p className="text-red-600 py-4">{loginActivityError}</p>
                            ) : loginActivityList.length === 0 ? (
                                <p className="text-gray-500 py-8">{t('profile.noLoginActivity')}</p>
                            ) : (
                                <>
                                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        {t('profile.loginDatetime')}
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        {t('profile.ipAddress')}
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        {t('profile.location')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {loginActivityList
                                                    .slice(
                                                        (loginActivityPage - 1) * loginActivityPageSize,
                                                        loginActivityPage * loginActivityPageSize
                                                    )
                                                    .map((row, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                                                {row.login_datetime
                                                                    ? new Date(row.login_datetime).toLocaleString()
                                                                    : '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600">{row.ip_address || '—'}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                                {row.location
                                                                    ? (typeof row.location === 'string'
                                                                        ? row.location
                                                                        : row.location?.formatted_address || JSON.stringify(row.location))
                                                                    : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mt-4">
                                        <Pagination
                                            currentPage={loginActivityPage}
                                            totalPages={Math.max(1, Math.ceil(loginActivityList.length / loginActivityPageSize))}
                                            totalCount={loginActivityList.length}
                                            currentCount={loginActivityList.slice(
                                                (loginActivityPage - 1) * loginActivityPageSize,
                                                loginActivityPage * loginActivityPageSize
                                            ).length}
                                            onPageChange={setLoginActivityPage}
                                            onPageSizeChange={(size) => {
                                                setLoginActivityPageSize(size);
                                                setLoginActivityPage(1);
                                            }}
                                            pageSize={loginActivityPageSize}
                                            showPageSize={true}
                                            pageSizeOptions={[5, 10]}
                                            forceVisible={true}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Session status – in-card, clear design */}
                    {tokenExpiration && (
                        <div
                            className={`
                                mx-8 mb-8 rounded-2xl border overflow-hidden
                                ${tokenExpiration === 'Expired'
                                    ? 'bg-red-50/80 border-red-200'
                                    : 'bg-slate-50/80 border-slate-200'
                                }
                            `}
                        >
                            <div className="p-5 flex flex-wrap items-center gap-5">
                                <div
                                    className={`
                                        flex items-center justify-center w-14 h-14 rounded-2xl shrink-0
                                        ${tokenExpiration === 'Expired'
                                            ? 'bg-red-100 text-red-600'
                                            : 'bg-emerald-100 text-emerald-600'
                                        }
                                    `}
                                >
                                    {tokenExpiration === 'Expired' ? (
                                        <Warning className="w-8 h-8" />
                                    ) : (
                                        <CheckCircle className="w-8 h-8" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-900">
                                        {tokenExpiration === 'Expired' ? t('profile.sessionExpired') : t('profile.sessionActive')}
                                    </div>
                                    <div className={`text-sm mt-0.5 ${tokenExpiration === 'Expired' ? 'text-red-700' : 'text-gray-600'}`}>
                                        {tokenExpiration === 'Expired'
                                            ? t('profile.pleaseLoginAgain')
                                            : `${tokenExpiration} ${t('profile.remaining')}`
                                        }
                                    </div>
                                    {tokenExpiration !== 'Expired' && sessionProgress != null && (
                                        <div className="mt-3 max-w-xs">
                                            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
                                                    style={{ width: `${sessionProgress.percentRemaining}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setLogoutDialogOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-red-500/90 text-white rounded-xl hover:bg-red-500 transition-colors border border-red-400/50 shrink-0"
                                >
                                    <Logout className="w-5 h-5" />
                                    {t('nav.logout')}
                                </button>
                            </div>
                        </div>
                    )}

            </PageCard>

                {/* Change Password Dialog */}
                <ChangePasswordDialog
                    open={passwordDialogOpen}
                    onClose={() => setPasswordDialogOpen(false)}
                    onSuccess={handlePasswordSuccess}
                    onError={handlePasswordError}
                />

                <LogoutConfirmDialog
                    open={logoutDialogOpen}
                    onClose={() => setLogoutDialogOpen(false)}
                    onConfirm={handleLogoutConfirm}
                />

                <NotificationPopup
                    open={snackbar.open}
                    onClose={handleClose}
                    severity={snackbar.severity}
                    message={snackbar.message}
                />
        </PageShell>
    );
};

export default Profile;